// Экран «Связи» (до 2026-10-05 — «Предшественники») — четвёртый экран нового фронта
// (п.20, 2026-09-07). Схема: блоки работ и стрелки между ними; блок таскается мышью,
// от кружка справа тянется новая связь, плюсик добавляет последователя, карандаш —
// карточка работы, клик по подписи стрелки — тип/задержка/сдвиг этажей/мягкая.
// В обычном проекте — один набор «Базовый» без понятия шаблонов; в проекте
// «Глобально» — библиотека шаблонов (свод, шаблон, черновик) и список связей.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ask } from '../dialog';
import { call, p, type Data } from '../api';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';
import { KAL_LABEL, RASK_LABEL } from './Works';
import { kontury } from '../util';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type TplRow = Data<'/api/link-templates/{tid}/rows', 'get'>[number];
type SvodRow = Data<'/api/link-templates/rows', 'get'>[number];
type Tpl = Data<'/api/link-templates', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];

export type Edge = {
  id: string | number; work: string; pred: string; tip: string;
  zapas?: number | null; operezhenie?: number | null; soft?: boolean;
  kom?: string;
  tpl?: string | null; foreign?: boolean;
};
type Node = { id: string; name: string; ghost?: boolean; dim?: boolean; tpl?: string | null };
type Mode = 'svod' | 'tpl' | 'draft';
type Draft = { name: string; links: Edge[]; extra: string[]; seq: number };
type Pos = Record<string, { x: number; y: number }>;

const NW = 200;
const POS_KEY = 'plot_gpos';
function readPos(key = POS_KEY): Pos {
  try { return JSON.parse(localStorage.getItem(key) || '{}') as Pos; } catch { return {}; }
}
function savePos(pos: Pos, key = POS_KEY) {
  try { localStorage.setItem(key, JSON.stringify(pos)); } catch { /* приватный режим */ }
}
/** Раскладка по уровням: предшественник левее, одинаковый уровень — столбиком */
function layout(nodes: Node[], edges: Edge[], pos: Pos) {
  const lvl: Record<string, number> = {};
  nodes.forEach(n => { lvl[n.id] = 0; });
  for (let k = 0; k < nodes.length; k++) {
    let changed = false;
    edges.forEach(e => {
      const cand = (lvl[e.pred] ?? 0) + 1;
      if (lvl[e.work] != null && cand > lvl[e.work]) { lvl[e.work] = cand; changed = true; }
    });
    if (!changed) break;
  }
  const cols: Record<number, number> = {};
  nodes.slice().sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)).forEach(n => {
    const L = lvl[n.id] || 0;
    cols[L] = cols[L] || 0;
    if (!pos[n.id]) pos[n.id] = { x: 40 + L * 270, y: 56 + cols[L] * 80 };   // ниже кнопок «＋» и «⟲» в углу
    cols[L]++;
  });
}
function edgeText(e: Edge, mode: Mode): string {
  let t = e.tip || 'ОН';
  if (e.zapas) t += ' +' + e.zapas + ' дн';
  if (e.operezhenie) t += ' ↑' + e.operezhenie + ' эт';
  if (e.soft) t += ' · мягкая';
  if (e.kom) t += ' 📝';
  if (e.foreign) return 'из «' + e.tpl + '» · ' + t;
  if (mode === 'svod' && e.tpl) t += ' · ' + e.tpl;
  return t;
}

/** tolkoRaskatki — схема встроена в панель «Типовой этаж» «Модели ГПР» (владелец 2026-10-05:
    «функционал как сейчас на связях, только внизу с отфильтрованными работами»): на схеме все
    работы этих раскаток, даже без связей, и только связи между ними; своя память раскладки;
    vysota — высота области схемы */
/** дорожки (владелец 2026-10-06, «дорожки по контурам — для текущего представления в типовом
    этаже»): работы раскладываются полосами по контуру планирования, порядок полос — как в
    «Списках»; блоки внутри полосы — по уровням связей; ручного перетаскивания нет */
const DOROZHKA_SHAPKA = 34, DOROZHKA_SHAG = 80, DOROZHKA_OTSTUP = 20;
function dorozhkiLayout(nodes: Node[], edges: Edge[], konturOf: (name: string) => string, poryadok: string[]) {
  const lvl: Record<string, number> = {};
  nodes.forEach(n => { lvl[n.id] = 0; });
  for (let k = 0; k < nodes.length; k++) {
    let changed = false;
    edges.forEach(e => {
      const cand = (lvl[e.pred] ?? 0) + 1;
      if (lvl[e.work] != null && cand > lvl[e.work]) { lvl[e.work] = cand; changed = true; }
    });
    if (!changed) break;
  }
  const poDorozhke = new Map<string, Node[]>();
  nodes.forEach(n => { const k = konturOf(n.name); (poDorozhke.get(k) || poDorozhke.set(k, []).get(k)!).push(n); });
  const imena = [...poDorozhke.keys()].sort((a, b) => {
    const ia = poryadok.findIndex(x => x.toLowerCase() === a.toLowerCase()), ib = poryadok.findIndex(x => x.toLowerCase() === b.toLowerCase());
    if (a === '— без контура —') return 1;
    if (b === '— без контура —') return -1;
    if (ia !== ib) return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
    return a.localeCompare(b, 'ru');
  });
  const pos: Pos = {};
  const dorozhki: { imya: string; y: number; h: number }[] = [];
  let y = 8, maxL = 0;
  imena.forEach(imya => {
    const cols: Record<number, number> = {};
    let rows = 1;
    poDorozhke.get(imya)!.slice().sort((a, b) => (lvl[a.id] - lvl[b.id]) || a.name.localeCompare(b.name, 'ru')).forEach(n => {
      const Lv = lvl[n.id] || 0;
      cols[Lv] = cols[Lv] || 0;
      pos[n.id] = { x: 40 + Lv * 270, y: y + DOROZHKA_SHAPKA + cols[Lv] * DOROZHKA_SHAG };
      cols[Lv]++;
      rows = Math.max(rows, cols[Lv]);
      maxL = Math.max(maxL, Lv);
    });
    const h = DOROZHKA_SHAPKA + rows * DOROZHKA_SHAG + DOROZHKA_OTSTUP;
    dorozhki.push({ imya, y, h });
    y += h;
  });
  return { pos, dorozhki, vysota: Math.max(y + 40, 300), shirina: Math.max(2600, 40 + (maxL + 1) * 270 + 60) };
}

export function Links({ ctx, tolkoRaskatki, vysota = '72vh', dorozhki }: {
  ctx: ShellCtx; tolkoRaskatki?: Set<string>; vysota?: string;
  /** дорожки по полю работы (пока только контур) — без ручного перетаскивания */
  dorozhki?: 'kontur';
}) {
  const posKey = tolkoRaskatki ? POS_KEY + '_tipet_' + ctx.pid : POS_KEY;
  const [works, setWorks] = useState<Work[]>([]);
  const [konturySpiska, setKonturySpiska] = useState<string[]>([]);   // порядок дорожек — как в «Списках»
  const [tpls, setTpls] = useState<Tpl[]>([]);
  const [tplId, setTplId] = useState(0);
  const [rows, setRows] = useState<TplRow[]>([]);
  const [svod, setSvod] = useState<SvodRow[]>([]);
  const [extra, setExtra] = useState<string[]>([]);   // работы, добавленные на схему вручную
  const [draft, setDraft] = useState<Draft | null>(null);
  const [mode, setMode] = useState<Mode>('svod');
  const [isGlobal, setIsGlobal] = useState(false);
  // всё загружено: встроенная схема показывает работы и без связей, и разложенные до
  // прихода связей блоки вставали одним столбиком — раскладываем только после загрузки
  const [gotovo, setGotovo] = useState(false);
  const [msg, setMsg] = useState('');
  const [pop, setPop] = useState<
    | { kind: 'add' | 'copy'; at: Anchor }
    | { kind: 'node' | 'plus'; name: string; at: Anchor }
    | { kind: 'edge'; e: Edge; at: Anchor }
    | null>(null);
  const [, bump] = useState(0);
  // выбранная работа — сведения о ней в панели под схемой (владелец 2026-10-05)
  const [vybrana, setVybrana] = useState<string | null>(null);
  const pos = useRef<Pos>(readPos(posKey));
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const can = ctx.canPlan;

  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  const saved = () => { ctx.onSaved?.(); setMsg(''); };

  const loadRows = useCallback(async (tid: number) => {
    if (!tid) { setRows([]); return; }
    const r = await call<TplRow[]>('get', p('/api/link-templates/{tid}/rows', { tid }));
    if (!r.ok) { setMsg(r.error.message); return; }
    setRows(r.data);
  }, []);

  const load = useCallback(async () => {
    const [w, t, s, pr] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid),
      call<SvodRow[]>('get', '/api/link-templates/rows?project=' + ctx.pid),
      call<Project[]>('get', '/api/projects'),
    ]);
    if (w.ok) setWorks(w.data);
    if (s.ok) setSvod(s.data);
    if (dorozhki) {
      const sp = await call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid }));
      if (sp.ok) setKonturySpiska(sp.data.filter(x => x.kod === 'kontur').map(x => x.znachenie));
    }
    let list = t.ok ? t.data : [];
    const gp = pr.ok ? pr.data.find(x => x.code === 'global') : undefined;
    const glob = !!gp && gp.id === ctx.pid;
    setIsGlobal(glob);
    if (!glob) {
      // обычный проект: один набор предшественников — шаблон «Базовый», всегда правка
      let base = list.find(x => x.name.toLowerCase() === 'базовый');
      if (!base && can) {
        const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: ctx.pid });
        if (c.ok) {
          const t2 = await call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid);
          if (t2.ok) { list = t2.data; base = list.find(x => x.id === c.data.id); }
        }
      }
      setTpls(list);
      if (base) { setTplId(base.id); setMode('tpl'); await loadRows(base.id); }
      setGotovo(true);
      return;
    }
    setTpls(list);
    if (mode === 'tpl' && tplId) await loadRows(tplId);
    setGotovo(true);
  }, [ctx.pid, can, loadRows, mode, tplId]);
  // перечитывать при смене проекта; смена шаблона обрабатывается отдельно
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx.pid]);

  // перечитать связи и шаблоны (без пересоздания «Базового»)
  const perechitat = useCallback(async () => {
    const s = await call<SvodRow[]>('get', '/api/link-templates/rows?project=' + ctx.pid);
    if (s.ok) setSvod(s.data);
    const t = await call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid);
    if (t.ok) setTpls(t.data);
    if (mode === 'tpl') await loadRows(tplId);
  }, [ctx.pid, mode, tplId, loadRows]);
  // reloadToken — сигнал оболочки «данные изменились» (связь добавлена из карточки
  // работы); без него схема не видела её до перезагрузки страницы (#14, 2026-09-12)
  const pervyjToken = useRef(ctx.reloadToken);
  useEffect(() => {
    if (ctx.reloadToken === pervyjToken.current) return;   // первый рендер уже загрузил load()
    void perechitat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.reloadToken]);
  // своя правка: оболочка по onChanged сама даст новый reloadToken — второй раз не перечитываем
  const reload = async () => { if (ctx.onChanged) ctx.onChanged(); else await perechitat(); };

  // встроенная схема: какие работы допустимы (null — все)
  const dopusk = useMemo(() => tolkoRaskatki
    ? new Set(works.filter(w => tolkoRaskatki.has(w.raskatka || 'этажи')).map(w => w.name)) : null,
  [works, tolkoRaskatki]);

  // ---- что показываем: свои узлы/связи режима + чужие связи пунктиром ----
  const ownEdges: Edge[] = useMemo(() => {
    if (mode === 'draft') return draft ? draft.links : [];
    if (mode === 'tpl') return rows.map(r => ({ id: r.id, work: r.work, pred: r.pred, tip: r.tip,
      zapas: r.zapas, operezhenie: r.operezhenie, soft: !!r.soft, kom: r.kom || '' }));
    return svod.map((e, i) => ({ id: 'sv' + i, work: e.work, pred: e.pred, tip: e.tip,
      zapas: e.zapas, operezhenie: e.oper, tpl: e.tpl, soft: !!e.soft, kom: e.kom || '' }));
  }, [mode, draft, rows, svod]);

  const ownNodes: Node[] = useMemo(() => {
    const names = new Set<string>(mode === 'draft' ? (draft ? draft.extra : []) : mode === 'tpl' ? extra : []);
    if (mode === 'svod') svod.forEach(e => { names.add(e.work); names.add(e.pred); });
    else ownEdges.forEach(e => { names.add(e.work); names.add(e.pred); });
    if (dopusk) { dopusk.forEach(n => names.add(n)); return [...names].filter(n => dopusk.has(n)).sort().map(n => ({ id: n, name: n })); }
    return [...names].sort().map(n => ({ id: n, name: n }));
  }, [mode, draft, extra, svod, ownEdges, dopusk]);

  const tplName = tpls.find(t => t.id === tplId)?.name || '';
  const foreign: Edge[] = useMemo(() => {
    if (mode === 'svod') return [];
    const own = mode === 'tpl' ? tplName : null;
    const onNames = new Set(ownNodes.map(n => n.name));
    const ownPairs = new Set(ownEdges.map(e => e.work + '¶' + e.pred));
    const seen = new Set<string>();
    return svod.filter(e => {
      if (own !== null && e.tpl === own) return false;
      if (!onNames.has(e.work)) return false;
      const k = e.work + '¶' + e.pred;
      if (ownPairs.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    }).map((e, i) => ({ id: 'f' + i, work: e.work, pred: e.pred, tip: e.tip, zapas: e.zapas,
      operezhenie: e.oper, soft: !!e.soft, kom: e.kom || '', tpl: e.tpl, foreign: true }));
  }, [mode, tplName, ownNodes, ownEdges, svod]);

  const view = useMemo(() => {
    const nodes: Node[] = [...ownNodes];
    if (mode !== 'svod') {
      const have = new Set(nodes.map(n => n.name));
      foreign.forEach(e => {
        if (!have.has(e.pred)) { have.add(e.pred); nodes.push({ id: e.pred, name: e.pred, ghost: true, tpl: e.tpl }); }
      });
    }
    let edges = mode === 'svod' ? ownEdges : ownEdges.concat(foreign);
    if (dopusk) {   // только связи между работами этажа, без «призраков» других циклов
      edges = edges.filter(e => dopusk.has(e.work) && dopusk.has(e.pred));
      const ost = nodes.filter(n => dopusk.has(n.name)).map(n => ({ ...n, ghost: false }));
      nodes.length = 0; nodes.push(...ost);
    }
    if (!ctx.filterActive || !ctx.matchesWork) return { nodes, edges };
    const focus = new Set(nodes.filter(n => ctx.matchesWork!(n.name)).map(n => n.id));
    const show = new Set(focus);
    edges.forEach(e => { if (focus.has(e.work) || focus.has(e.pred)) { show.add(e.work); show.add(e.pred); } });
    return {
      nodes: nodes.filter(n => show.has(n.id)).map(n => (focus.has(n.id) ? n : { ...n, dim: true })),
      edges: edges.filter(e => show.has(e.work) && show.has(e.pred)),
    };
  }, [ownNodes, ownEdges, foreign, mode, ctx, dopusk]);
  const zhdyom = !!tolkoRaskatki && !gotovo;
  // дорожки: координаты считаются заново при каждом изменении схемы, в память не пишутся
  const dor = useMemo(() => {
    if (!dorozhki || zhdyom) return null;
    const poImeni = new Map(works.map(w => [w.name, w]));
    const konturOf = (name: string) => kontury(poImeni.get(name)?.kontur, konturySpiska)[0] || '— без контура —';
    return dorozhkiLayout(view.nodes, view.edges, konturOf, konturySpiska);
  }, [dorozhki, zhdyom, view, works, konturySpiska]);
  if (!zhdyom && !dor) layout(view.nodes, view.edges, pos.current);
  const koord: Pos = dor ? dor.pos : pos.current;
  const holstW = dor ? dor.shirina : 2600, holstH = dor ? dor.vysota : 2200;

  // ---- перетаскивание блоков и вытягивание связи от кружка ----
  const drag = useRef<{ id: string; dx: number; dy: number; x0: number; y0: number; sdvinut: boolean } | null>(null);
  const linkFrom = useRef<string | null>(null);
  const [temp, setTemp] = useState('');
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const xy = (ev: { clientX: number; clientY: number }) => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };
  const anchor = (id: string, side: 'in' | 'out') => {
    const q = koord[id] || { x: 0, y: 0 };
    const el = canvas.current?.querySelector<HTMLElement>('.g-node[data-id="' + CSS.escape(id) + '"]');
    const h = el ? el.offsetHeight : 46;
    return side === 'out' ? { x: q.x + NW, y: q.y + h / 2 } : { x: q.x, y: q.y + h / 2 };
  };
  useEffect(() => {
    const move = (ev: MouseEvent) => {
      if (drag.current) {
        const c = xy(ev), d = drag.current;
        if (!d.sdvinut && Math.hypot(c.x - d.x0, c.y - d.y0) < 4) return;   // ещё щелчок, не перетаскивание
        d.sdvinut = true;
        pos.current[d.id] = { x: Math.max(0, c.x - d.dx), y: Math.max(0, c.y - d.dy) };
        bump(v => v + 1);
      } else if (linkFrom.current) {
        const a = anchor(linkFrom.current, 'out'), c = xy(ev);
        setTemp('M ' + a.x + ' ' + a.y + ' C ' + (a.x + 50) + ' ' + a.y + ', ' + (c.x - 50) + ' ' + c.y + ', ' + c.x + ' ' + c.y);
        const over = (ev.target as HTMLElement).closest('.g-node') as HTMLElement | null;
        setDropTarget(over && over.dataset.id !== linkFrom.current ? over.dataset.id! : null);
      }
    };
    const up = (ev: MouseEvent) => {
      if (drag.current) {
        const d = drag.current; drag.current = null;
        if (d.sdvinut) savePos(pos.current, posKey); else setVybrana(d.id);   // щелчок — показать работу внизу
        return;
      }
      if (linkFrom.current) {
        const from = linkFrom.current;
        linkFrom.current = null; setTemp(''); setDropTarget(null);
        const target = (ev.target as HTMLElement).closest('.g-node') as HTMLElement | null;
        if (target && target.dataset.id !== from) void createLink(target.dataset.id!, from);
      }
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  function nodeDown(ev: React.MouseEvent, id: string, ghost?: boolean) {
    const t = ev.target as HTMLElement;
    if (t.closest('.gplus') || t.closest('.gedit')) return;
    if (t.closest('.port')) { linkFrom.current = id; ev.preventDefault(); return; }
    if (ghost && mode !== 'svod') { /* призрак тоже таскаем */ }
    const c = xy(ev), q = koord[id];
    if (dor) { setVybrana(id); ev.preventDefault(); return; }   // дорожки: блоки не таскаются, щелчок — выбрать
    drag.current = { id, dx: c.x - q.x, dy: c.y - q.y, x0: c.x, y0: c.y, sdvinut: false };
    ev.preventDefault();
  }

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !pop) setVybrana(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [pop]);

  // ---- правки связей ----
  async function createLink(work: string, pred: string) {
    if (mode === 'svod' || !can) return;
    if (mode === 'draft' && draft) {
      if (draft.links.some(e => e.work === work && e.pred === pred)) return;
      setDraft({ ...draft, seq: draft.seq + 1,
        links: [...draft.links, { id: 'd' + draft.seq, work, pred, tip: 'ОН', zapas: 0, operezhenie: 0, soft: false }] });
      setMsg('Черновик «' + draft.name + '»: связей ' + (draft.links.length + 1) + ' — не забудьте «Сохранить шаблон».');
      return;
    }
    const r = await call('post', p('/api/link-templates/{tid}/rows', { tid: tplId }), { work, pred });
    if (!r.ok) { fail(r.error.message); return; }
    saved(); await reload();
  }
  async function saveEdge(e: Edge, v: { tip: string; zapas: string; oper: string; soft: boolean; kom: string }) {
    setPop(null);
    if (mode === 'draft' && draft) {
      setDraft({ ...draft, links: draft.links.map(x => (x.id === e.id
        ? { ...x, tip: v.tip, zapas: +v.zapas.replace(',', '.') || 0, operezhenie: +v.oper.replace(',', '.') || 0,
            soft: v.soft, kom: v.kom }
        : x)) });
      return;
    }
    const r = await call('patch', p('/api/template-rows/{rid}', { rid: e.id as number }),
      { tip: v.tip, zapas: v.zapas || 0, operezhenie_etazhej: v.oper || 0, soft: v.soft, kom: v.kom });
    if (!r.ok) { fail(r.error.message); return; }
    saved(); await reload();
  }
  async function delEdge(e: Edge) {
    setPop(null);
    if (mode === 'draft' && draft) { setDraft({ ...draft, links: draft.links.filter(x => x.id !== e.id) }); return; }
    const r = await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number }));
    if (!r.ok) { fail(r.error.message); return; }
    saved(); await reload();
  }
  async function swapNode(name: string, nw: Work) {
    setPop(null);
    const mine = ownEdges.filter(e => e.work === name || e.pred === name);
    if (mode === 'draft' && draft) {
      setDraft({ ...draft, extra: draft.extra.map(x => (x === name ? nw.name : x)),
        links: draft.links.map(e => ({ ...e, work: e.work === name ? nw.name : e.work, pred: e.pred === name ? nw.name : e.pred })) });
      return;
    }
    for (const e of mine) {
      await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number }));
      const r = await call('post', p('/api/link-templates/{tid}/rows', { tid: tplId }), {
        work: e.work === name ? nw.name : e.work, pred: e.pred === name ? nw.name : e.pred,
        tip: e.tip || 'ОН', zapas: e.zapas || 0, operezhenie: e.operezhenie || 0, kom: e.kom || '' });
      if (!r.ok) fail(r.error.message);
    }
    setExtra(x => (mine.length ? x.filter(n => n !== name) : x.filter(n => n !== name).concat(nw.name)));
    saved(); await reload();
  }
  async function removeNode(name: string) {
    setPop(null);
    const mine = ownEdges.filter(e => e.work === name || e.pred === name);
    if (mode === 'draft' && draft) {
      setDraft({ ...draft, extra: draft.extra.filter(x => x !== name),
        links: draft.links.filter(e => e.work !== name && e.pred !== name) });
      return;
    }
    if (mine.length && !confirm('У работы ' + mine.length + ' связ. в шаблоне — удалить их?')) return;
    for (const e of mine) await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number }));
    setExtra(x => x.filter(n => n !== name));
    saved(); await reload();
  }
  function addToBoard(name: string) {
    if (mode === 'draft' && draft) setDraft({ ...draft, extra: [...draft.extra, name] });
    else setExtra(x => (x.includes(name) ? x : [...x, name]));
  }

  // ---- шаблоны (только проект «Глобально») ----
  async function pickTpl(v: string) {
    if (v === 'new') {
      const name = (await ask('Название нового шаблона (например «Фасад НВФ»):') || '').trim();
      if (!name) return;
      setDraft({ name, links: [], extra: [], seq: 1 }); setMode('draft');
      setMsg('Черновик «' + name + '»: добавьте работы плюсиком в углу схемы, нарисуйте связи и нажмите «Сохранить шаблон».');
      return;
    }
    if (v) {
      const tid = +v;
      setTplId(tid); setExtra([]); setMode('tpl'); setDraft(null);
      await loadRows(tid);
      setMsg('Правки шаблона сразу меняют связи графика — после изменений нажмите «Пересчитать» на «Модели».');
    } else { setMode('svod'); setDraft(null); setMsg(''); }
  }
  async function saveDraft() {
    if (!draft) return;
    if (!draft.links.length) { setMsg('В черновике пока нет связей.'); return; }
    const r = await call<{ id: number; svyazey: number }>('post', '/api/link-templates', {
      name: draft.name, project: ctx.pid,
      links: draft.links.map(e => ({ work: e.work, pred: e.pred, tip: e.tip, zapas: e.zapas || 0,
        operezhenie: e.operezhenie || 0, soft: !!e.soft, kom: e.kom || '' })) });
    if (!r.ok) { fail(r.error.message); return; }
    setDraft(null);
    await pickTpl(String(r.data.id));
    await reload();
    setMsg('Шаблон сохранён (' + r.data.svyazey + ' св.) и уже действует на график — нажмите «Пересчитать» на «Модели».');
  }
  async function delTpl() {
    if (!tplId) return;
    if (!confirm('Удалить шаблон «' + tplName + '»? Его связи уйдут из графика проекта.')) return;
    const r = await call('delete', p('/api/link-templates/{tid}', { tid: tplId }));
    if (!r.ok) { fail(r.error.message); return; }
    setTplId(0); setMode('svod');
    await reload();
    setMsg('Шаблон удалён — его связи больше не действуют. Нажмите «Пересчитать».');
  }

  const editable = mode !== 'svod' && can;
  // выбранная работа (есть на схеме) и все её связи — без учёта фильтра сверху
  const vybr = vybrana ? view.nodes.find(n => n.id === vybrana) || null : null;
  const vseRebra = mode === 'svod' ? ownEdges : ownEdges.concat(foreign);

  if (zhdyom) return <div className="pw-muted" style={{ padding: 12 }}>Загружаю работы и связи…</div>;
  return (
    <div>
      {/* полоса над схемой — только для шаблонов «Глобально» и сообщений: подсказку из неё
          убрали (владелец 2026-10-05), пустой она оставалась серым прямоугольником */}
      {(isGlobal || msg) && <div className="bar actbar">
        {isGlobal && <>
          <label>Шаблон связей{' '}
            <select style={{ minWidth: 240 }} value={mode === 'draft' ? '' : String(tplId || '')} onChange={e => void pickTpl(e.target.value)}>
              <option value="">Все связи графика</option>
              {tpls.map(t => <option key={t.id} value={t.id}>{t.name + ' (' + t.svyazey + ' св.)'}</option>)}
              {can && <option value="new">+ Новый шаблон…</option>}
            </select></label>
          {mode === 'tpl' && can && <button className="del" onClick={() => void delTpl()}>Удалить шаблон</button>}
          {mode === 'draft' && <button className="sec" onClick={() => void saveDraft()}>Сохранить шаблон «{draft?.name}»</button>}
          {can && <button className="sec" title="Скопировать шаблон из другого проекта"
            onClick={e => setPop({ kind: 'copy', at: at(e) })}>Из другого проекта…</button>}
        </>}
        <span className="pw-muted">{msg}</span>
        <span className="grow" />
      </div>}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 10, top: 10, zIndex: 5, display: 'flex', gap: 6 }}>
          {editable && <button title="Добавить работу на схему" onClick={e => setPop({ kind: 'add', at: at(e) })}>＋</button>}
          {!dor && <button className="sec" title="Сбросить раскладку блоков"
            onClick={() => { pos.current = {}; savePos({}, posKey); bump(v => v + 1); }}>⟲</button>}
        </div>
        <div ref={wrap} style={{ position: 'relative', height: vybr ? 'calc(' + vysota + ' - 230px)' : vysota, overflow: 'auto',
          border: '1px solid var(--plot-line)', borderRadius: 8, background: '#fbfcfe' }}>
          <div ref={canvas} style={{ position: 'relative', width: holstW, height: holstH }}
            onMouseDown={ev => {
              const t = ev.target as Element;
              if (!t.closest('.g-node') && !t.closest('.e-lbl')) setVybrana(null);   // щелчок мимо — закрыть
            }}>
            <svg width={holstW} height={holstH} style={{ position: 'absolute', left: 0, top: 0 }}>
              {dor && dor.dorozhki.map((d, i) => (
                <g key={d.imya} className="dorozhka">
                  <rect x={0} y={d.y} width={holstW} height={d.h} fill={i % 2 ? '#f3f5f9' : '#fbfcfe'} />
                  <line x1={0} x2={holstW} y1={d.y} y2={d.y} stroke="var(--plot-line)" />
                  <text x={i === 0 ? 66 : 12} y={d.y + 22} className="dorozhka-imya">{d.imya}</text>
                </g>))}
              <defs>
                <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
                </marker>
              </defs>
              <g>{view.edges.map(e => {
                const a = anchor(e.pred, 'out'), b = anchor(e.work, 'in');
                const dx = Math.max(40, Math.abs(b.x - a.x) / 2);
                const txt = edgeText(e, mode);
                const wpx = txt.length * 6.5 + 14;
                const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 4;
                // связи выбранной работы ярче, остальные бледнее — видно, с чем она связана
                const svoya = !!vybr && (e.work === vybr.id || e.pred === vybr.id);
                return (
                  <g key={String(e.id)} opacity={vybr && !svoya ? 0.22 : undefined}>
                    <path d={'M ' + a.x + ' ' + a.y + ' C ' + (a.x + dx) + ' ' + a.y + ', ' + (b.x - dx) + ' ' + b.y + ', ' + b.x + ' ' + b.y}
                      stroke={e.foreign ? '#98a2b3' : e.soft ? '#f79009' : e.tip === 'НН' ? '#7c3aed' : '#64748b'}
                      strokeWidth={svoya ? 3 : e.foreign ? 1.4 : 1.8} fill="none" markerEnd="url(#arr)"
                      strokeDasharray={e.foreign ? '6 4' : e.soft ? '5 5' : undefined} opacity={e.foreign ? 0.7 : undefined} />
                    <g className={'e-lbl' + (e.foreign ? ' foreign' : '')}
                      onClick={ev => { if (editable && !e.foreign) setPop({ kind: 'edge', e, at: at(ev) }); }}>
                      <rect x={mx - wpx / 2} y={my - 10} width={wpx} height={18} rx={7} />
                      <text x={mx} y={my + 3} textAnchor="middle">{txt}</text>
                    </g>
                  </g>);
              })}</g>
              {temp && <path d={temp} stroke="#1a56db" strokeWidth={2} fill="none" strokeDasharray="5 4" />}
            </svg>
            {view.nodes.map(n => (
              <div key={n.id} className={'g-node' + (n.ghost ? ' ghost' : '') + (n.dim ? ' dim' : '') + (dropTarget === n.id ? ' droptarget' : '')
                + (vybr?.id === n.id ? ' gsel' : '')}
                data-id={n.id} style={{ left: koord[n.id]?.x ?? 0, top: koord[n.id]?.y ?? 0 }}
                title={n.ghost ? 'Предшественник назначен в шаблоне «' + n.tpl + '» — здесь дублировать не нужно'
                  : n.dim ? 'сосед выбранных работ (вне фильтра)'
                  : dor ? 'Клик — сведения о работе внизу; двойной клик — карточка работы; тяните за кружок — связь. Дорожка — контур планирования работы'
                  : 'Клик — сведения о работе внизу; двойной клик — карточка работы; тяните за кружок — связь'}
                onMouseDown={ev => nodeDown(ev, n.id, n.ghost)}
                onDoubleClick={() => { if (!n.ghost) ctx.onWorkCard?.(n.name); }}>
                {n.name}
                {editable && !n.ghost && <>
                  <div className="port" title="потяните к следующей работе" />
                  <div className="gplus" title="добавить последователя"
                    onClick={ev => setPop({ kind: 'plus', name: n.name, at: at(ev) })}>+</div>
                  <div className="gedit" title="карточка работы: заменить или убрать со схемы"
                    onClick={ev => setPop({ kind: 'node', name: n.name, at: at(ev) })}>✎</div>
                </>}
              </div>
            ))}
            {!view.nodes.length && <div className="g-empty">{ctx.filterActive
              ? 'Под фильтр сверху не попала ни одна работа со связями — измените фильтр или нажмите «Сбросить».'
              : mode === 'svod' ? 'В шаблонах проекта пока нет связей — выберите или создайте шаблон.'
                : 'Пока нет предшественников — добавьте работу плюсиком в углу схемы и потяните стрелку, или задайте их тегами в «Работах».'}</div>}
          </div>
        </div>
      </div>

      {isGlobal && mode === 'tpl' && <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Предшественники списком</summary>
        <AddLinkForm works={works} can={can} onAdd={async v => {
          const r = await call('post', p('/api/link-templates/{tid}/rows', { tid: tplId }), v);
          if (!r.ok) { fail(r.error.message); return; }
          saved(); await reload();
        }} />
        <div className="tbl" style={{ maxHeight: '40vh' }}><table>
          <thead><tr><th>Работа</th><th>Предшественник</th><th>Тип</th><th>Задержка, дн</th><th>Сдвиг этажей</th>
            <th title="Зачем связь и почему такая задержка">Комментарий</th><th></th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id}>
              <td>{r.work}</td><td>{r.pred}</td><td>{r.tip}</td>
              <td className="num">{r.zapas || ''}</td><td className="num">{r.operezhenie || ''}</td>
              <td className="pw-muted" title={r.kom || ''}>{r.kom || ''}</td>
              <td>{can && <button className="del" onClick={() => void delEdge({ id: r.id, work: r.work, pred: r.pred, tip: r.tip })}>×</button>}</td>
            </tr>))}</tbody>
        </table></div>
        <div className="pw-muted" style={{ marginTop: 8 }}>ОН — начнётся после окончания предшественника; НН — вместе с его началом.
          Замкнутые круги сервис не пропустит.</div>
      </details>}

      {vybr && <RabotaPanel name={vybr.name} w={works.find(x => x.name === vybr.name)}
        preds={vseRebra.filter(e => e.work === vybr.id)} next={vseRebra.filter(e => e.pred === vybr.id)}
        editable={editable && !vybr.ghost}
        onPick={n => setVybrana(n)} onClose={() => setVybrana(null)}
        onCard={() => ctx.onWorkCard?.(vybr.name)}
        onEdge={(e, ev) => { if (editable && !e.foreign) setPop({ kind: 'edge', e, at: at(ev) }); }}
        onNext={ev => setPop({ kind: 'plus', name: vybr.name, at: at(ev) })} />}

      {pop?.kind === 'add' && <AddNodePop at={pop.at} works={dopusk ? works.filter(w => dopusk.has(w.name)) : works} on={new Set(ownNodes.map(n => n.name))}
        onClose={() => setPop(null)} onPick={n => { addToBoard(n); setPop(null); }} />}
      {pop?.kind === 'plus' && <AddNodePop at={pop.at} title={'После «' + pop.name + '»'}
        hint="Выберите работу, которая пойдёт следом (связь ОН — начнётся после окончания; тип и запас меняются кликом по стрелке)."
        works={works.filter(w => w.name !== pop.name && (!dopusk || dopusk.has(w.name)))} on={new Set(view.edges.filter(e => e.pred === pop.name).map(e => e.work))}
        onClose={() => setPop(null)} onPick={n => { addToBoard(n); void createLink(n, pop.name); setPop(null); }} />}
      {pop?.kind === 'node' && <NodePop at={pop.at} name={pop.name} works={works} on={new Set(ownNodes.map(n => n.name))}
        links={ownEdges.filter(e => e.work === pop.name || e.pred === pop.name).length}
        onClose={() => setPop(null)} onSwap={w => void swapNode(pop.name, w)} onRemove={() => void removeNode(pop.name)} />}
      {pop?.kind === 'edge' && <EdgePop at={pop.at} e={pop.e} onClose={() => setPop(null)}
        onSave={v => void saveEdge(pop.e, v)} onDelete={() => void delEdge(pop.e)} />}
      {pop?.kind === 'copy' && <CopyTplPop at={pop.at} pid={ctx.pid} onClose={() => setPop(null)} onFail={fail}
        onDone={async id => { setPop(null); await reload(); await pickTpl(String(id));
          setMsg('Шаблон скопирован и действует на график — нажмите «Пересчитать».'); }} />}
    </div>
  );
}

function AddLinkForm({ works, can, onAdd }: { works: Work[]; can: boolean;
  onAdd: (v: { work: string; pred: string; tip: string; zapas: number | string; operezhenie: number | string }) => void }) {
  const [v, setV] = useState({ work: '', pred: '', tip: 'ОН', zapas: '', oper: '' });
  if (!can) return null;
  return (
    <div className="bar" style={{ marginTop: 8 }}>
      <select style={{ maxWidth: 320 }} value={v.work} onChange={e => setV(s => ({ ...s, work: e.target.value }))}>
        <option value="">— работа —</option>{works.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}</select>
      <span>после</span>
      <select style={{ maxWidth: 320 }} value={v.pred} onChange={e => setV(s => ({ ...s, pred: e.target.value }))}>
        <option value="">— предшественник —</option>{works.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}</select>
      <select value={v.tip} onChange={e => setV(s => ({ ...s, tip: e.target.value }))}>
        <option value="ОН">окончание → начало (ОН)</option><option value="НН">начало → начало (НН)</option></select>
      <input placeholder="задержка, дн" size={10} value={v.zapas} onChange={e => setV(s => ({ ...s, zapas: e.target.value }))} />
      <input placeholder="сдвиг этажей" size={10} value={v.oper} onChange={e => setV(s => ({ ...s, oper: e.target.value }))} />
      <button onClick={() => { if (v.work && v.pred) onAdd({ work: v.work, pred: v.pred, tip: v.tip, zapas: v.zapas || 0, operezhenie: v.oper || 0 }); }}>
        Добавить связь</button>
    </div>
  );
}

function AddNodePop({ at: a, works, on, title = 'Работа на схему', hint, onClose, onPick }:
  { at: Anchor; works: Work[]; on: Set<string>; title?: string; hint?: string; onClose: () => void; onPick: (name: string) => void }) {
  const cand = works.filter(w => !on.has(w.name));
  const [v, setV] = useState('');
  return (
    <Pop at={a} title={title} onClose={onClose} width={320}>
      {hint && <div className="pw-muted" style={{ margin: '4px 0 8px' }}>{hint}</div>}
      {cand.length === 0
        ? <div className="pw-muted" style={{ margin: '8px 0' }}>Все работы проекта уже на схеме.</div>
        : <div style={{ margin: '10px 0' }}>
            <select style={{ maxWidth: 290 }} value={v} onChange={e => setV(e.target.value)}>
              <option value="">— выбрать работу —</option>
              {cand.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
            </select></div>}
      <div className="bar" style={{ margin: 0 }}><button onClick={() => { if (v) onPick(v); }}>Добавить</button></div>
    </Pop>
  );
}

function NodePop({ at: a, name, works, on, links, onClose, onSwap, onRemove }:
  { at: Anchor; name: string; works: Work[]; on: Set<string>; links: number; onClose: () => void;
    onSwap: (w: Work) => void; onRemove: () => void }) {
  const [v, setV] = useState('');
  const cand = works.filter(w => !on.has(w.name));
  return (
    <Pop at={a} title={name} onClose={onClose} width={330}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Связей у работы на схеме: {links}. Замена перенесёт их на другую работу.</div>
      <div style={{ margin: '0 0 8px' }}>
        <select style={{ maxWidth: 300 }} value={v} onChange={e => setV(e.target.value)}>
          <option value="">— заменить на работу… —</option>
          {cand.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select></div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => { const w = works.find(x => x.id === +v); if (w) onSwap(w); }}>Заменить</button>
        <button className="del" onClick={onRemove}>Убрать со схемы</button>
      </div>
    </Pop>
  );
}

export function EdgePop({ at: a, e, onClose, onSave, onDelete }:
  { at: Anchor; e: Edge; onClose: () => void;
    onSave: (v: { tip: string; zapas: string; oper: string; soft: boolean; kom: string }) => void; onDelete: () => void }) {
  const [v, setV] = useState({ tip: e.tip || 'ОН', zapas: e.zapas ? String(e.zapas) : '',
    oper: e.operezhenie ? String(e.operezhenie) : '', soft: !!e.soft, kom: e.kom || '' });
  return (
    <Pop at={a} title={e.pred + ' → ' + e.work} onClose={onClose} width={460}>
      {/* компактно (владелец 2026-10-07: «карточку связи переработай»): одна строка полей,
          пояснения — в подсказках, кнопки «Закрыть» нет — щелчок мимо или Esc */}
      <div className="ep-polya">
        <label className="ep-pole" title="Окончание → начало: работа начнётся после окончания предшественника. Начало → начало: вместе с его началом">
          <span>Тип</span>
          <select value={v.tip} onChange={ev => setV(s => ({ ...s, tip: ev.target.value }))}>
            <option value="ОН">после окончания</option><option value="НН">вместе с началом</option></select></label>
        <label className="ep-pole" title="Пауза после предшественника, дней"><span>Задержка, дн</span>
          <input value={v.zapas} placeholder="0" onChange={ev => setV(s => ({ ...s, zapas: ev.target.value }))} /></label>
        <label className="ep-pole" title="Предшественник берётся с этажа на N выше (минус — ниже)"><span>Сдвиг, эт.</span>
          <input value={v.oper} placeholder="0" onChange={ev => setV(s => ({ ...s, oper: ev.target.value }))} /></label>
        <label className="ep-pole ep-myag" title="Мягкая связь дату не держит — задаёт только порядок этажей"><span>Мягкая</span>
          <input type="checkbox" checked={v.soft} onChange={ev => setV(s => ({ ...s, soft: ev.target.checked }))} /></label>
      </div>
      <input className="ep-kom" value={v.kom} placeholder="Комментарий (на расчёт не влияет)"
        title="Пояснение для себя и коллег: видно в подсказке связи и в карточке работы"
        onChange={ev => setV(s => ({ ...s, kom: ev.target.value }))}
        onKeyDown={ev => { if (ev.key === 'Enter') onSave(v); }} />
      <div className="bar" style={{ margin: '8px 0 0' }}>
        <button onClick={() => onSave(v)}>Сохранить</button>
        <button className="del" onClick={onDelete}>Удалить</button>
      </div>
    </Pop>
  );
}

function CopyTplPop({ at: a, pid, onClose, onDone, onFail }:
  { at: Anchor; pid: number; onClose: () => void; onDone: (id: number) => void; onFail: (m: string) => void }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [proj, setProj] = useState('');
  const [list, setList] = useState<Tpl[]>([]);
  const [tid, setTid] = useState('');
  useEffect(() => {
    void (async () => {
      const r = await call<Project[]>('get', '/api/projects');
      if (!r.ok) { onFail(r.error.message); return; }
      const others = r.data.filter(x => x.id !== pid);
      setProjects(others);
      if (others.length) setProj(String(others[0].id));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!proj) return;
    void (async () => {
      const r = await call<Tpl[]>('get', '/api/link-templates?project=' + proj);
      if (r.ok) { setList(r.data); setTid(r.data.length ? String(r.data[0].id) : ''); }
    })();
  }, [proj]);
  async function copy() {
    if (!tid) return;
    const src = list.find(x => String(x.id) === tid);
    const name = (await ask('Название копии в этом проекте:', src?.name || '') || '').trim();
    if (!name) return;
    const r = await call<{ id: number }>('post', '/api/link-templates', { name, project: pid, copy_of: +tid });
    if (!r.ok) { onFail(r.error.message); return; }
    onDone(r.data.id);
  }
  return (
    <Pop at={a} title="Шаблон из другого проекта" onClose={onClose} width={330}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        <label>Проект<br />
          <select style={{ maxWidth: 300 }} value={proj} onChange={e => setProj(e.target.value)}>
            {projects.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
        <label>Шаблон<br />
          <select style={{ maxWidth: 300 }} value={tid} onChange={e => setTid(e.target.value)}>
            {list.map(t => <option key={t.id} value={t.id}>{t.name + ' (' + t.svyazey + ' св.)'}</option>)}</select></label>
        <div className="bar" style={{ margin: 0 }}><button onClick={() => void copy()}>Скопировать</button></div>
      </div>
    </Pop>
  );
}

/** Связь словами для панели: «после окончания · +2 дн · сдвиг +2 эт. · мягкая» */
export function svyazSlovami(e: Edge): string {
  const t = [e.tip === 'НН' ? 'вместе с началом' : 'после окончания'];
  if (e.zapas) t.push((e.zapas > 0 ? '+' : '') + e.zapas + ' дн');
  if (e.operezhenie) t.push('сдвиг ' + (e.operezhenie > 0 ? '+' : '') + e.operezhenie + ' эт.');
  if (e.soft) t.push('мягкая');
  if (e.foreign && e.tpl) t.push('из «' + e.tpl + '»');
  return t.join(' · ');
}

/** Панель под схемой: что за работа и с чем связана (владелец 2026-10-05: «при нажатии
    на работу в связях пусть снизу появляется окно с информацией по работе»).
    Поля — только посмотреть, правка — в карточке работы. Имя соседа переводит выбор
    на него, подпись связи открывает окно правки связи. */
function RabotaPanel({ name, w, preds, next, editable, onPick, onClose, onCard, onEdge, onNext }: {
  name: string; w?: Work; preds: Edge[]; next: Edge[]; editable: boolean;
  onPick: (n: string) => void; onClose: () => void; onCard: () => void;
  onEdge: (e: Edge, ev: React.MouseEvent) => void; onNext: (ev: React.MouseEvent) => void;
}) {
  const pole = (lab: string, val: React.ReactNode) => (
    <div className="rp-pole"><span className="rp-lab">{lab}</span><span className="rp-val">{val || '—'}</span></div>);
  const spisok = (zag: string, rebra: Edge[], kogo: 'pred' | 'work') => (
    <div className="rp-svyazi">
      <div className="rp-zag">{zag} · {rebra.length}</div>
      {rebra.length ? rebra.map(e => (
        <div key={String(e.id)} className="rp-sv">
          <a className="rp-imya" title="Показать эту работу" onClick={() => onPick(e[kogo])}>{e[kogo]}</a>
          <a className={'rp-tip' + (editable && !e.foreign ? ' prav' : '')}
            title={editable && !e.foreign ? 'Изменить связь: тип, задержка, сдвиг этажей' : undefined}
            onClick={ev => onEdge(e, ev)}>{svyazSlovami(e)}</a>
        </div>)) : <div className="pw-muted">нет</div>}
    </div>);
  return (
    <div className="rp">
      <div className="rp-shapka">
        <b className="rp-nazv">{name}</b>
        {w?.rab_name && <span className="pw-muted">на площадке: {w.rab_name}</span>}
        <span className="grow" />
        {editable && <button className="sec" onClick={onNext}>+ следующая работа</button>}
        {w && <button className="sec" onClick={onCard}>Карточка работы</button>}
        <button className="sec" title="Закрыть (Esc)" aria-label="Закрыть" onClick={onClose}>✕</button>
      </div>
      <div className="rp-telo">
        {w ? <div className="rp-polya">
          {pole('Раскатка', RASK_LABEL[w.raskatka] || w.raskatka)}
          {pole('Длительность', w.dlit != null ? w.dlit + ' дн' : '')}
          {pole('Логика монтажа', w.logika)}
          {pole('Календарь', KAL_LABEL[w.kalendar] || w.kalendar)}
          {pole('Этап', w.etap)}
          {pole('Контур', w.kontur)}
          {pole('Группа работ', w.sistema)}
          {pole('Корпуса', w.korpusa || 'все')}
          {pole('Тепловой контур', w.tk ? 'да' : 'нет')}
          {pole('Задач в графике', w.zadach)}
        </div> : <div className="pw-muted">Работы нет в справочнике проекта — связь пришла из другого шаблона.</div>}
        {spisok('Идёт после', preds, 'pred')}
        {spisok('Следом идут', next, 'work')}
      </div>
    </div>
  );
}
